'use client';

import { App } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { AssetLogsService } from '~/app/services/goldenRecord/asset-logs.service';

export type NormalizeLogsParams = {
  clientName: string;
  fromDate?: string;
  toDate?: string;
};

export type UseNormalizeAssetLogsResult = {
  isPending: boolean;
  normalizeLogs: (params: NormalizeLogsParams) => Promise<void>;
};

export function useNormalizeAssetLogs(
  organizationId: string,
  assetId: string,
): UseNormalizeAssetLogsResult {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const { t } = useTranslation('assets');

  const mutation = useMutation({
    mutationFn: (params: NormalizeLogsParams) => {
      const payload = {
        clientName: params.clientName,
        fromDate: params.fromDate
          ? dayjs(params.fromDate).format('YYYY/MM/DD')
          : dayjs().subtract(1, 'month').format('YYYY/MM/DD'),
        toDate: params.toDate
          ? dayjs(params.toDate).format('YYYY/MM/DD')
          : dayjs().format('YYYY/MM/DD'),
        batchSize: 50,
        isTreated: false,
      };
      return AssetLogsService.normalizeLogs(organizationId, assetId, payload);
    },
    onSuccess: async () => {
      message.success(t('detail.logs.actions.normalizeSuccess'));
      await queryClient.invalidateQueries({
        queryKey: ['asset-logs', organizationId, assetId],
      });
    },
    onError: (error) => {
      message.error(
        error instanceof Error
          ? error.message
          : t('detail.logs.actions.normalizeError'),
      );
    },
  });

  return {
    isPending: mutation.isPending,
    normalizeLogs: mutation.mutateAsync,
  };
}
